"use client";

import { Check } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function WhyProfessionalTestingMatters() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: tableRef,
    isVisible: tableVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: ctaRef,
    isVisible: ctaVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const comparisonRows = [{
    criterion: "DETECTION SENSITIVITY",
    standard: "Visual inspection only; often misses hidden mold colonies.",
    prism: "Advanced air sampling and moisture mapping for total detection."
  }, {
    criterion: "REPORTING SPEED",
    standard: "Delayed results; often takes weeks for lab processing.",
    prism: "Rapid, certified lab analysis with same-day preliminary data."
  }, {
    criterion: "CERTIFICATION",
    standard: "Unlicensed assessments with no legal standing.",
    prism: "Licensed, insured, and certified environmental testing."
  }, {
    criterion: "AIR QUALITY DATA",
    standard: "Subjective smell-based assessment; no empirical proof.",
    prism: "Quantitative particle analysis and spore count verification."
  }, {
    criterion: "HEALTH IMPACT",
    standard: "Ignores potential toxicity and long-term exposure risks.",
    prism: "Comprehensive risk assessment for respiratory safety."
  }, {
    criterion: "EXPERT GUIDANCE",
    standard: "Generic advice without actionable remediation steps.",
    prism: "Evidence-based protocols for safe, effective remediation."
  }, {
    criterion: "RELIABILITY",
    standard: "High margin of error; prone to false negatives.",
    prism: "Clinical-grade precision with 26 years of proven expertise."
  }];
  return <section data-section-id="2561" id="why-professional-testing-matters" className="bg-background text-foreground py-20 md:py-28 px-4 sm:px-6 lg:px-12">
      <div className="max-w-7xl mx-auto">
        {}
        <div ref={headerRef} className={`relative mb-16 md:mb-20 transition-all duration-700 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-end">
            <div className="lg:col-span-8">
              <p className="font-mono text-xs tracking-widest text-primary mb-6">
                03 — CLINICAL COMPARISON
              </p>
              <h2 className="font-default text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tighter text-foreground">
                Why professional testing matters.
              </h2>
            </div>
            <div className="lg:col-span-4 lg:text-right">
              <p className="font-mono text-[10px] tracking-widest text-muted-foreground leading-relaxed">
                EVIDENCE-FORWARD
                <br />
                <span className="text-primary">/</span> SOUTH FLORIDA
              </p>
            </div>
          </div>
        </div>

        {}
        <div ref={tableRef} className={`transition-all duration-700 ease-out delay-100 ${tableVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          {}
          <div className="grid grid-cols-1 md:grid-cols-2 border-t border-border">
            <div className="px-6 py-8 md:py-10 border-b md:border-b-0 md:border-r border-border">
              <p className="font-mono text-xs tracking-widest text-muted-foreground mb-2">
                LEFT — A
              </p>
              <h3 className="font-default text-sm md:text-base tracking-widest uppercase text-muted-foreground font-semibold">
                Standard Inspection
              </h3>
            </div>
            <div className="px-6 py-8 md:py-10 bg-accent/40">
              <div className="flex items-center gap-2 mb-2">
                <span className="inline-block w-1.5 h-1.5 rounded-lg bg-primary"></span>
                <p className="font-mono text-xs tracking-widest text-primary">
                  RIGHT — B
                </p>
              </div>
              <h3 className="font-default text-sm md:text-base tracking-widest uppercase text-foreground font-bold">
                MYB Services Professional Testing
              </h3>
            </div>
          </div>

          {}
          <div className="border-t border-border">
            {comparisonRows.map((row, i) => <div data-index={i} key={i} className={`grid grid-cols-1 md:grid-cols-[200px_1fr_1fr] border-b border-border ${i % 2 === 1 ? "bg-muted/30" : ""}`}>
                {}
                <div className="px-6 pt-6 md:py-8 md:border-r border-border flex items-start md:items-center">
                  <p className="font-mono text-[10px] md:text-xs tracking-widest uppercase text-muted-foreground leading-relaxed">
                    {row.criterion}
                  </p>
                </div>

                {}
                <div className="px-6 py-4 md:py-8 md:border-r border-border">
                  <p className="font-default text-sm md:text-base text-muted-foreground leading-relaxed">
                    {row.standard}
                  </p>
                </div>

                {}
                <div className="px-6 py-4 md:py-8 pb-6 md:pb-8">
                  <div className="flex items-start gap-3">
                    <span className="mt-1.5 inline-flex items-center justify-center shrink-0">
                      <Check className="w-3.5 h-3.5 text-primary" strokeWidth={2.5} />
                    </span>
                    <p className="font-default text-sm md:text-base text-foreground font-semibold leading-relaxed">
                      {row.prism}
                    </p>
                  </div>
                </div>
              </div>)}
          </div>
        </div>

        {}
        <div ref={ctaRef} className={`mt-16 md:mt-20 flex flex-col items-center gap-4 transition-all duration-700 ease-out delay-200 ${ctaVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <Link to="/ContactPage">
            <Button size="lg" className="bg-primary text-white hover:bg-primary/90 font-default text-xs tracking-widest uppercase px-8 py-6 h-auto rounded-lg shadow-md">
              Schedule Your Inspection — (561) 560-MOLD
            </Button>
          </Link>
          <p className="font-mono text-[10px] tracking-widest text-muted-foreground mt-2">
            SERVING DADE, BROWARD & WEST PALM <span className="text-primary">/</span> 26 YEARS EXPERIENCE
          </p>
        </div>
      </div>
    </section>;
}